import { getComposedPathWithTeleport } from "@/helpers/utility/DOM";
import { unrefElement } from "@vueuse/core";
import type { MaybeComputedElementRef } from "@vueuse/core";

/**
 * Calls the callback when the user clicks outside of the given elements.
 */
export function onPointerDownOutside(
  elementsInside: MaybeComputedElementRef[],
  callback: (event: PointerEvent, unbind: () => void) => void
) {
  function onMouseDown(pointerEvent: PointerEvent) {
    const unwrappedElements = elementsInside.map((el) => unrefElement(el));
    const path = pointerEvent.composedPath();
    if (
      !path.some((node) =>
        unwrappedElements.includes(node as HTMLElement | SVGElement)
      ) 
      // only clicks inside the app root are considered
      && path.some((node) => (node as Element).id == "app")
    ) {
      callback(pointerEvent, unbind);
    }
  }

  window.addEventListener("pointerdown", onMouseDown, {
    capture: true
  });

  function unbind() {
    window.removeEventListener("pointerdown", onMouseDown, {
      capture: true
    });
  }

  return unbind;
}

export function onClickOutside(
  elementsInside: MaybeComputedElementRef[],
  callback: (event: MouseEvent, unbind: () => void) => void,
  options: { capture?: boolean } = {}
) {
  function onClick(mouseEvent: MouseEvent) {
    const unwrappedElements = elementsInside.map((el) => unrefElement(el));
    const standardPath = mouseEvent.composedPath();
    const path = getComposedPathWithTeleport(standardPath);
    if (
      !path.some((node) =>
          unwrappedElements.includes(node as HTMLElement | SVGElement)
        )
    ) {
      callback(mouseEvent, unbind);
    }
  }

  window.addEventListener("click", onClick, {
    capture: options.capture ?? true
  });

  function unbind() {
    window.removeEventListener("click", onClick, {
      capture: options.capture ?? true
    });
  }

  return unbind;
}
